import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import WalletService from "../../services/WalletService";
import { validateTopUpInput, parseTopUpError } from "../../utils/topUpErrorHandler";
import { resolvePaymentProvider } from "../../utils/paymentMethods";

import applePayIcon from "../../assets/icons/payment-methods/apple-pay.svg";
import cardIcon from "../../assets/icons/payment-methods/credit-card.png";
import googlePayIcon from "../../assets/icons/payment-methods/g-pay.png";
import paypalIcon from "../../assets/icons/payment-methods/paypal.png";
import blikIcon from "../../assets/icons/payment-methods/blik-seeklogo.png";
import stripeIcon from "../../assets/icons/payment-methods/stripe.png";

import "./ProfileModals.css";
import { trackAnalyticsEvent } from "../../analytics/analytics";
import { appendCampaignParamsToUrl } from "../../analytics/campaignTracker";
import { AuthContext } from "../../context/AuthContext";

const PENDING_WALLET_TOPUP_PROVIDER_KEY = "pending_wallet_topup_provider";

const TopUpModal = ({ isOpen, onClose, walletId, currency, onToppedUp, geoCountryCode, geoReady }) => {
  const { userId } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  // Top-up amount selection
  const [selectedAmount, setSelectedAmount] = useState(null);

  // Payment method selection
  const [paymentOption, setPaymentOption] = useState("card");

  // Voucher validation state
  const [voucher, setVoucher] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isVoucherValid, setIsVoucherValid] = useState(null);
  const [topUpAmounts, setTopUpAmounts] = useState([]);

  // General top-up form state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  const isPolishLanguage = (i18n.language || "").toLowerCase().startsWith("pl");
  const shouldCheckGeo = !isPolishLanguage;
  const showBlik = isPolishLanguage || (geoReady && geoCountryCode === "PL");

  useEffect(() => {
    const loadAmounts = async () => {
      if (isOpen && currency) {
        const amounts = await WalletService.getTopUpAmounts(currency);
        setTopUpAmounts(amounts);
        setSelectedAmount(amounts[0] || 5);
      }
    };

    loadAmounts();
  }, [isOpen, currency]);

  useEffect(() => {
    if (!showBlik && paymentOption === "blik") {
      setPaymentOption("card");
    }
  }, [showBlik, paymentOption]);

  const resetVoucherState = () => {
    setVoucher("");
    setPromoDiscount(0);
    setIsVoucherValid(null);
  };

  const resetState = () => {
    setSelectedAmount(5);
    setPaymentOption("card");
    resetVoucherState();
    setError("");
    setSuccess("");
    setSending(false);
  };

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen]);

  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    if (promoDiscount > 0 && amount < promoDiscount) {
      setPromoDiscount(0);
      setIsVoucherValid(null);
    }
    setError("");
  };

  const handleSelectMethod = (method) => {
    if (paymentOption === method) return;
    setPaymentOption(method);
  };

  const handleCloseWithRefresh = async () => {
    if (success) {
      onToppedUp();
    }
    onClose();
  };

  const finalAmount = promoDiscount > 0 && isVoucherValid === true ? selectedAmount - promoDiscount : selectedAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateTopUpInput({
      selectedAmount,
      voucher,
      promoDiscount,
      currency,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    const provider = resolvePaymentProvider(paymentOption);

    try {
      setSending(true);

      const payload = {
        request: {
          wallet_id: walletId,
          amount: finalAmount,
          currency,
          provider: provider,
          promocode: isVoucherValid === true ? voucher.trim() : null,
        },
        external: {
          frontend_url: appendCampaignParamsToUrl(`${window.location.origin}/${currentLang}/account`),
          product_name: "Wallet Top-Up",
        },
      };

      const response = await WalletService.topUp(payload);

      const stripeMatch = response.redirect_url?.match(/(cs_[a-zA-Z0-9_]+)/);
      const stripeSessionId = stripeMatch ? stripeMatch[1] : "";

      // Подготавливаем данные для события аналитики
      const pendingAnalyticsData = {
        transaction_id: String(response.transaction_id || response.id || `topup_${Date.now()}`),
        stripe_session_id: stripeSessionId,
        payment_id: String(response.payment_id || response.id || ""),
        user_id: String(userId || ""),
        value: Number(finalAmount),
        price: Number(finalAmount),
        currency: currency || "EUR",
        coupon: isVoucherValid === true ? voucher.trim() : "",
        payment_type: paymentOption, // card, apple_google_pay, paypal, blik, stripe
        purchase_type: "wallet_topup",
        item_id: "wallet_balance",
        item_name: "Wallet Top-Up",
        item_category: "Wallet",
        quantity: 1,
      };

      if (response.success && response.redirect_url) {
        try {
          if (provider === "paypal") {
            localStorage.setItem(PENDING_WALLET_TOPUP_PROVIDER_KEY, provider);
          } else {
            localStorage.removeItem(PENDING_WALLET_TOPUP_PROVIDER_KEY);
          }
        } catch (storageError) {
          console.error("Failed to save wallet top-up payment provider:", storageError);
        }
        localStorage.setItem("pending_wallet_topup", JSON.stringify(pendingAnalyticsData));
        window.location.href = response.redirect_url;
        return;
      }

      if (response.success) {
        resetVoucherState();
        setSuccess(
          `Top-up successful! +${response.topuped_amount?.toLocaleString() || finalAmount} ${response.currency || currency}`,
        );
        trackAnalyticsEvent.walletTopUp(pendingAnalyticsData);
        return;
      }
    } catch (err) {
      const friendlyError = parseTopUpError(err);
      setError(friendlyError);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={sending ? () => {} : handleCloseWithRefresh}
      title={t("profile.topUpWallet")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={sending}>
            {t("common.cancel")}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={sending || !selectedAmount}>
            {sending ? t("cart.processing") : t("profile.topUp")}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        {/* Selection buttons */}
        <div className="profile-topup-amounts">
          {topUpAmounts.map((amount) => (
            <button
              type="button"
              key={amount}
              className={`profile-topup-amount-button ${selectedAmount === amount ? "selected" : ""}`}
              onClick={() => handleAmountClick(amount)}
              disabled={sending}
            >
              {amount} {currency}
            </button>
          ))}
        </div>

        {/* Payment Methods Section */}
        <label className="profile-topup-method-label">
          {t("cart.paymentMethod")}

          <div className={`payment-methods-group ${sending ? "disabled-area" : ""}`}>
            <div className="payment-methods-row payment-methods-row--two">
              <button
                type="button"
                className={`payment-method-tile ${paymentOption === "card" ? "payment-method-tile--active" : ""}`}
                onClick={() => handleSelectMethod("card")}
                disabled={sending}
                aria-label="Card"
                title="Card"
              >
                <img src={cardIcon} alt="Card" className="payment-method-tile__icon payment-method-tile__icon--card" />
                <span className="payment-method-tile__label">Card</span>
              </button>

              <button
                type="button"
                className={`payment-method-tile payment-method-tile--logo-only ${
                  paymentOption === "apple_google_pay" ? "payment-method-tile--active" : ""
                }`}
                onClick={() => handleSelectMethod("apple_google_pay")}
                disabled={sending}
                aria-label="Apple Pay / Google Pay"
                title="Apple Pay / Google Pay"
              >
                <div className="payment-method-tile__logos-inline">
                  <img
                    src={applePayIcon}
                    alt="Apple Pay"
                    className="payment-method-tile__icon payment-method-tile__icon--applepay"
                  />
                  <span className="payment-method-tile__slash">/</span>
                  <img
                    src={googlePayIcon}
                    alt="Google Pay"
                    className="payment-method-tile__icon payment-method-tile__icon--gpay"
                  />
                </div>
              </button>
            </div>

            <div className="payment-methods-row payment-methods-row--two">
              <button
                type="button"
                className={`payment-method-tile payment-method-tile--logo-only ${
                  paymentOption === "paypal" ? "payment-method-tile--active" : ""
                }`}
                onClick={() => handleSelectMethod("paypal")}
                disabled={sending}
                aria-label="PayPal"
                title="PayPal"
              >
                <img
                  src={paypalIcon}
                  alt="PayPal"
                  className="payment-method-tile__icon payment-method-tile__icon--wide"
                />
              </button>

              {showBlik ? (
                <button
                  type="button"
                  className={`payment-method-tile payment-method-tile--logo-only ${
                    paymentOption === "blik" ? "payment-method-tile--active" : ""
                  }`}
                  onClick={() => handleSelectMethod("blik")}
                  disabled={sending}
                  aria-label="BLIK"
                  title="BLIK"
                >
                  <img
                    src={blikIcon}
                    alt="BLIK"
                    className="payment-method-tile__icon payment-method-tile__icon--wide"
                  />
                </button>
              ) : !geoReady && shouldCheckGeo ? (
                <div
                  className="payment-method-tile payment-method-tile--logo-only payment-method-tile--placeholder"
                  aria-hidden="true"
                />
              ) : (
                <button
                  type="button"
                  className={`payment-method-tile payment-method-tile--logo-only ${
                    paymentOption === "stripe" ? "payment-method-tile--active" : ""
                  }`}
                  onClick={() => handleSelectMethod("stripe")}
                  disabled={sending}
                  aria-label="Stripe"
                  title="Stripe"
                >
                  <img
                    src={stripeIcon}
                    alt="Stripe"
                    className="payment-method-tile__icon payment-method-tile__icon--wide"
                  />
                </button>
              )}
            </div>
          </div>
        </label>

        {isVoucherValid === true && promoDiscount > 0 && (
          <div className="profile-promo-discount">
            You'll pay: {(selectedAmount - promoDiscount).toFixed(2)} {currency} (promo: -{promoDiscount.toFixed(2)}{" "}
            {currency})
          </div>
        )}

        {/* Top-up form error/success */}
        {error && (
          <div className="profile-topup-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-topup-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default TopUpModal;
